// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, EMPTY, merge, Observable, of } from "rxjs"
import { Action } from "redux"
import { grpc } from "../../../app/rpc/executor"
import { siteClient } from "../../../app/rpc/client"
import { catchError, filter, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Slice from "../slice"
import { SiteId } from "../slice"
import { GetSiteRequest, Site } from "../../../generated/legebill/site/v4/site_pb"
import { never } from "grpc-web-rx"
import * as TagsActions from "../../tags/actions"
import * as TagsSlice from "../../tags/slice"
import * as UserContextActions from "../../user-context/actions"
import { StatusCode } from "grpc-web"
import { isGrpcError } from "../../../lib/rpcutils"
import { combineEpics } from "redux-observable"
import { RootState } from "../../../app/store"

type GetSiteRpc = (siteId: SiteId) => Observable<Site>

/**
 * Refresh site details usually called when time posting errors that arise when time posting requirements and/or
 * preferences at the site become out of sync.
 */
export const refreshSiteDetails = (getSiteRpc: GetSiteRpc) => (action$: Observable<Action>): Observable<Action> =>
  action$.pipe(
    filter(UserContextActions.refreshSiteDetails.match),
    mergeMap(({ payload: { siteId }}) =>
      getSiteRpc(siteId)
        .pipe(
          mergeMap(site => of(Slice.setCurrentSite(site.toObject()))),
          catchError(e => handleError(e, siteId))
        )
    )
  )

/**
 * Called when the current site id is updated (triggered in turn when user and memberships details are set / updated).
 */
export const getSite = (getSiteRpc: GetSiteRpc) => (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
  action$.pipe(
    filter(Slice.setCurrentSiteId.match),
    withLatestFrom(state$),
    mergeMap(([{ payload: { siteId }}, state]) =>
      getSiteRpc(siteId)
        .pipe(
          mergeMap(site =>
            merge(
              of(Slice.setCurrentSite(site.toObject())),
              state.userContext.currentSite?.id === siteId ? EMPTY : fetchUserPreferencesForSite(site.getId()),
            )
          ),
          catchError(e => handleError(e, siteId))
        )
    )
  )

const fetchUserPreferencesForSite = (siteId: SiteId): Observable<Action> =>
  concat(
    // clear cache of recent and favourite tags
    of(TagsSlice.clearRecentAndFavouriteTags()),
    // fetch recent and favourite tags to cache
    merge(
      of(TagsActions.fetchRecentTags({ swallowFailure: true })),
      of(TagsActions.fetchFavouriteTags({ siteId, swallowFailure: true })),
    )
  )

const getSiteRpc = (siteId: SiteId): Observable<Site> =>
  grpc(() => siteClient.getSite(new GetSiteRequest().setId(siteId), null), never)

const handleError = (e: unknown, siteId: SiteId): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND || code === StatusCode.PERMISSION_DENIED) {
      // clear current site id and re-fetch user (includes sites list) and membership details
      return concat(
        of(Slice.clearCurrentSiteId()),
        of(UserContextActions.fetchUserDetails()),
      )
    }
  }

  // Even though if getting site info failed we will keep dispatching actions to fetch site-related user preferences
  return merge(
    of(UserContextActions.getSiteFailed()),
    fetchUserPreferencesForSite(siteId)
  )
}

export default combineEpics<Action, Action, RootState, unknown>(
  refreshSiteDetails(getSiteRpc),
  getSite(getSiteRpc)
)
